import type { GarmentGeometry, PieceGrain } from '@seddi/scene';
import {
  type BufferAttribute,
  type Material,
  type MeshPhysicalMaterial,
  type Object3D,
  type Texture,
  BackSide,
  BufferGeometry,
  LineBasicMaterial,
  LineSegments,
  Matrix3,
  Mesh,
  MeshStandardMaterial,
  WireframeGeometry,
} from '@seddi/three';

import {
  computeVertexNormalsF32,
  createIndex,
  createVertexAttributeF32,
} from '../core/bufferGeometry';
import * as FlagsModule from '../flags/flagsModule';
import { FeatFlag } from '../flags/flagTypes';
import { create as createStitchesTexturesMaterial } from '../materials/stitchesTexturesMaterial';
import type { GarmentGeometryDesc } from '../resources/garmentGeometryTypes';
import { clone as cloneMaterial } from '../resources/material';
import type { ResourcesContext } from '../resources/resourcesContext';
import type { StitchesTextures } from '../resources/stitchesTexturesTypes';
import { TPieceGeometryEx } from '../scene/sceneTypes';
import { PieceButtonRenderable, dispose as disposeButton } from './pieceButtonRenderable';
import {
  type PieceLogoRenderable,
  dispose as disposeLogoInternal,
  updateVisibility,
} from './pieceLogoRenderable';
import { textureMapsKeys } from '../resources/textureTypes';

const FrontMaterialDef = new MeshStandardMaterial({ color: '#C4C4C4' });
const BackMaterialDef = new MeshStandardMaterial({ color: '#C4C4C4' });
BackMaterialDef.side = BackSide;

export type PieceRenderable = {
  /** Entity ID */
  readonly id: string;
  readonly buttons: PieceButtonRenderable[];
  fabricRef?: string;
  garmentGeometry?: GarmentGeometry;
  geometry: BufferGeometry;
  readonly logos: PieceLogoRenderable[];
  readonly mesh: Mesh;
  /** Stitches decals */
  stitchesMaterials?: MeshPhysicalMaterial[];
  stitchesTexturesRef?: string;
};

/**
 * Create piece renderable.
 * @param id - ID.
 * @param node - Scene node.
 */
export const create = (id: string, node: Object3D): PieceRenderable => {
  const geometry = new BufferGeometry();
  // Render back face, then front face.
  const materials = [BackMaterialDef.clone(), FrontMaterialDef.clone()];
  const mesh = new Mesh(geometry, materials);
  mesh.name = `${id}-mesh`;
  mesh.userData = { isPiece: true };
  node.add(mesh);
  return { buttons: [], geometry, id, logos: [], mesh };
};

/**
 * Destroy piece renderable.
 * @param renderable - Renderable.
 * @param resources - Resources context.
 */
export const dispose = (renderable: PieceRenderable, resources: ResourcesContext) => {
  // remove from scene
  const mesh = renderable.mesh;
  mesh.parent.remove(mesh);
  // detach stitches textures.
  detachStitchesTextures(renderable);
  // dispose geometry
  renderable.geometry.dispose();
  // dispose buttons
  const buttons = renderable.buttons;
  for (const button of buttons) {
    disposeButton(button, resources);
  }
  buttons.length = 0;
  // dispose logos
  const logos = renderable.logos;
  for (const logo of logos) {
    disposeLogoInternal(logo, resources);
  }
  logos.length = 0;
};

/**
 * Update fabric materials.
 * @param renderable - Renderable.
 * @param id - Fabric ID.
 * @param grain - Grain.
 * @param resources - Resources context.
 */
export const attachFabric = (
  renderable: PieceRenderable,
  id: string,
  grain: PieceGrain,
  resources: ResourcesContext,
) => {
  if (renderable.fabricRef) {
    detachFabric(renderable, resources);
  }

  // Create material instances.
  const fabric = resources.getRefFabric(id);
  const material0 = cloneMaterial(fabric.back || fabric.front);
  const material1 = cloneMaterial(fabric.front);
  material0.side = BackSide;
  const materials = renderable.mesh.material as unknown as Material[];
  materials[0].dispose();
  materials[1].dispose();
  materials[0] = material0;
  materials[1] = material1;
  renderable.fabricRef = id;

  updateGrain(renderable, grain);
};

/**
 * Detach fabric.
 * @param renderable - Renderable.
 * @param resources - Resources context.
 */
export const detachFabric = (renderable: PieceRenderable, resources: ResourcesContext) => {
  if (!renderable.fabricRef) {
    return;
  }
  const mesh = renderable.mesh;
  const materials = mesh.material as unknown as Material[];
  materials[0].dispose();
  materials[0] = FrontMaterialDef;
  materials[1].dispose();
  materials[1] = BackMaterialDef;
  resources.retRefFabric(renderable.fabricRef);
  delete renderable.fabricRef;
};

/**
 * Attach stitches textures.
 * @param renderable - Renderable.
 * @param stitchesTextures - Stitches textures.
 */
export const attachStitchesTextures = (
  renderable: PieceRenderable,
  uvs: Float32Array,
  stitchesTextures: StitchesTextures,
) => {
  if (!renderable.garmentGeometry) {
    return;
  }
  // find piece textures
  const id = renderable.id;
  const pieceTextures = stitchesTextures.pieces.find((p) => p.id === id);
  if (!pieceTextures) {
    return;
  }
  // create materials
  const stitchesMaterials = pieceTextures.materials.map((m) =>
    createStitchesTexturesMaterial(m, uvs),
  );
  renderable.stitchesMaterials = stitchesMaterials;
  // update groups and materials
  recreateMaterials(renderable);
  recreateGroups(renderable);
};

/**
 * Detach stitches textures.
 * @param renderable - Renderable.
 */
export const detachStitchesTextures = (renderable: PieceRenderable) => {
  if (!renderable.stitchesTexturesRef) {
    return;
  }
  // dispose materials
  for (const material of renderable.stitchesMaterials!) {
    material.dispose();
  }
  renderable.stitchesMaterials = undefined;
  renderable.stitchesTexturesRef = undefined;
  // update groups and materials
  recreateMaterials(renderable);
  recreateGroups(renderable);
};

/**
 * Update geometry from garment geometry.
 * @param renderable - Renderable.
 * @param garmentGeometry - Garment geometry.
 */
export const updateGeometryFromGarment = (
  renderable: PieceRenderable,
  garmentGeometry: GarmentGeometryDesc,
) => {
  const id = renderable.id;
  const data = garmentGeometry.pieces.find((p) => p.id === id)!;
  // Recreate.
  const geometry = renderable.geometry;
  createIndex(geometry, data.indices);
  createVertexAttributeF32(geometry, 'position', data.position3D, 3);
  createVertexAttributeF32(geometry, 'uv', data.position2D, 2);
  if (data.normals) {
    createVertexAttributeF32(geometry, 'normal', data.normals, 3);
  } else {
    computeVertexNormalsF32(geometry);
  }

  // TODO: Review implementation and do not do after recreate.
  if (FlagsModule.isEnabled(FeatFlag.WIREFRAME_FABRICS)) {
    updateGeometryWireframe(renderable, data);
  }

  renderable.garmentGeometry = garmentGeometry;
};

/**
 * Update geometry buffers for wireframe rendering.
 * @param renderable - Renderable.
 * @param data - Geometry data.
 */
export const updateGeometryWireframe = (() => {
  const _wireframeMaterial = new LineBasicMaterial({ color: 0x007722 });

  return (renderable: PieceRenderable, data: TPieceGeometryEx) => {
    const mesh = renderable.mesh;
    const wireframeObject = mesh.getObjectByName('wireframeVersion') as Mesh;
    if (wireframeObject) {
      // This does not works ok, it generates a lot of artifacts
      // const wfGeometry = wireframeObject.geometry;
      // updateIndes(wfGeometry, data.indices);
      // updateVertexAttributeF32(wfGeometry, 'uv', data.position2D, 2);
      // updateVertexAttributeF32(wfGeometry, 'position', data.position3D, 3);
      // if (data.normal) {
      //   updateVertexAttributeF32(wfGeometry, 'normal', data.normal, 3);
      // } else {
      //   computeVertexNormalsF32(wfGeometry);
      // }
      // geometry.computeBoundingSphere();
      // wireframeObject.geometry = mesh.geometry;

      //TODO: Performance - Create a new WireframeGeometry is not a cheap thing to do on every frame of an online simulation
      wireframeObject.geometry = new WireframeGeometry(mesh.geometry);
    } else {
      const geo = new WireframeGeometry(mesh.geometry);
      const wireframe = new LineSegments(geo, _wireframeMaterial);
      wireframe.name = 'wireframeVersion';
      // wireframe.material.opacity = 5;
      wireframe.material.transparent = true;
      mesh.add(wireframe);
    }
  };
})();

/**
 * Update grain.
 * @param renderable - Renderable.
 * @param grain - Grain.
 */
export const updateGrain = ((
  // Reusable matrices
  translationMatrix = new Matrix3(),
  rotationMatrix = new Matrix3(),
  scaleMatrix = new Matrix3(),
  flipMatrix = new Matrix3(),
  tmpMatrix = new Matrix3(),
) => {
  return (renderable: PieceRenderable, grain: PieceGrain): boolean => {
    const mesh = renderable.mesh;
    // Ensure we always work with an array of materials
    const materials: MeshPhysicalMaterial[] = Array.isArray(mesh.material)
      ? (mesh.material as MeshPhysicalMaterial[])
      : [mesh.material as MeshPhysicalMaterial];

    // Set up matrices to rotate about grain.origin.
    // First, translate so that grain.origin becomes (0,0)
    translationMatrix.makeTranslation(-grain.origin.x, -grain.origin.y);
    // Then, rotate by the negative grain orientation.
    rotationMatrix.makeRotation(-grain.orientation);
    // For back-side textures, define a horizontal flip.
    flipMatrix.makeScale(-1.0, 1.0);

    // Update every texture key found on each material.
    for (const material of materials) {
      if (!material.userData?.fabric) {
        continue;
      }

      for (const key of textureMapsKeys) {
        const texture = material[key] as Texture;
        if (!texture) {
          continue;
        }
        // Create a scale matrix from the texture's repeat.
        scaleMatrix.makeScale(texture.repeat.x, texture.repeat.y);

        if (material.side === BackSide) {
          tmpMatrix.copy(flipMatrix);
          tmpMatrix.multiply(scaleMatrix);
        } else {
          tmpMatrix.copy(scaleMatrix);
        }

        tmpMatrix.multiply(rotationMatrix).multiply(translationMatrix);

        texture.matrix.copy(tmpMatrix);
        texture.matrixAutoUpdate = false;
      }
    }
    return true;
  };
})();

/**
 * Recreate geometry groups.
 * @param renderable - Renderable.
 */
export const recreateGroups = (renderable: PieceRenderable) => {
  const mesh = renderable.mesh;
  const geometry = mesh.geometry;
  const material = mesh.material as Material[];
  // whole set of indices
  const start = 0;
  const index = geometry.index as BufferAttribute;
  const count = index.count;
  // clear
  geometry.clearGroups();
  // render with all available materials
  for (let i = 0; i < material.length; ++i) {
    geometry.addGroup(start, count, i);
  }
};

/**
 * Recreate materials array.
 * @param renderable - Renderable.
 */
export const recreateMaterials = (renderable: PieceRenderable) => {
  const material = renderable.mesh.material as Material[];
  // keep fabric materials
  material.length = 2;
  // push logo decals
  const logos = renderable.logos;
  for (const logo of logos) {
    if (logo.resourceRef) {
      material.push(logo.material);
    }
  }
  // push stitch decals
  const { garmentGeometry, stitchesMaterials } = renderable;
  if (garmentGeometry && stitchesMaterials) {
    material.push(...stitchesMaterials);
  }
};

/**
 * Dispose logo.
 * @param - Piece logo ID.
 */
export const disposeLogo = (
  renderable: PieceRenderable,
  id: string,
  resources: ResourcesContext,
) => {
  // remove logo
  const logos = renderable.logos;
  const index = logos.findIndex((logo) => logo.id === id);
  disposeLogoInternal(logos[index], resources);
  logos.splice(index, 1);
  // update groups and materials
  recreateMaterials(renderable);
  recreateGroups(renderable);
};

export const updateLogosVisibility = (renderable: PieceRenderable, visible: boolean) => {
  const pieceLogoRenderables = renderable.logos;
  for (const pieceLogoRenderable of pieceLogoRenderables) {
    updateVisibility(pieceLogoRenderable, visible);
  }
};
